import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "..";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IQueryParams } from "../common/common.types";
import { getSearchQuery } from "../common/helpers";
import { uploadFile, uploadMultipleFiles } from "../../helpers/uploadFile";
import { ITaskListItem, ITaskListState } from "./taskList.types";
import { omit } from "lodash";
import { IProject } from "../projects/project.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { generateAdvanceFilterQuery } from "../../hooks/useAdvanceFilterQuery";
import { IFileUpload } from "../../components/FileUpload/FileUpload.type";
import { getUniqueId } from "../../helpers";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";

export const FETCH_TASK_LIST_PROGRESS = "FETCH_TASK_LIST_PROGRESS";
export const FETCH_TASK_LIST_SUCCESS = "FETCH_TASK_LIST_SUCCESS";
export const FETCH_TASK_LIST_FAILED = "FETCH_TASK_LIST_FAILED";

export const fetchTaskListProgress = () => action(FETCH_TASK_LIST_PROGRESS);
export const fetchTaskListSuccess = (
  data: ITaskListState["list"],
  totalRecords: number,
) => action(FETCH_TASK_LIST_SUCCESS, { data, totalRecords });
export const fetchTaskListFailed = () => action(FETCH_TASK_LIST_FAILED);

export const fetchTaskListAsync =
  (
    queryParams: IQueryParams,
    billing_company_uuid: string,
    projectName?: string,
    assignTo?: string[],
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchTaskListProgress());

      const searchQuery = getSearchQuery(queryParams);

      let url = `/task/get-task-list${searchQuery}`;
      if (billing_company_uuid) {
        url += `&billing_company_uuid=${billing_company_uuid}`;
      }
      if (projectName && projectName.length > 0) {
        url += `&project_uuid=${projectName}`;
      }
      if (assignTo && assignTo.length > 0) {
        url += `&assignee_uuid=${JSON.stringify(assignTo)}`;
      }
      const res = await api.get(url);
      const data: ITaskListItem[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchTaskListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchTaskListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };
export const fetchTaskListNewAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    billing_company_uuid: string,
    searchState: { [key: string]: any },
    assignTo?: string[],
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchTaskListProgress());
      const { status, ...restSearchState } = searchState || {};
      const allFilters: any[] = [];

      if (status && status.length > 0) {
        allFilters.push({
          column: ["status"],
          operator: "IN",
          value: status,
          logicalOperator: "AND",
        });
      }
      // searchValue may already be an array of filter objects
      if (searchValue && searchValue.length > 0) {
        allFilters.push(...searchValue);
      }
      for (const [key, value] of Object.entries(restSearchState)) {
        if (value !== "" && value !== null) {
          let filterObj = {
            column: [key],
            operator: "CONTAINS",
            value,
            logicalOperator: "AND",
          };
          if (key === "transaction_date") {
            filterObj.value = value + "-";
          }
          allFilters.push(filterObj);
        }
      }
      let finalUrl = `/task/get-task-list?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (allFilters.length > 0) {
        const encoded = encodeURIComponent(JSON.stringify(allFilters, null, 2));
        finalUrl += `&advanceFilter=${encoded}`;
      }
      if (billing_company_uuid) {
        finalUrl += `&billing_company_uuid=${billing_company_uuid}`;
      }
      // if (projectName && projectName.length > 0) {
      //   finalUrl += `&project_uuid=${projectName}`;
      // }
      if (assignTo && assignTo.length > 0) {
        finalUrl += `&assignee_uuid=${assignTo.join(",")}`;
      }
      const res = await api.get(finalUrl);
      const data: ITaskListItem[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      const updatedTasks = data.map((task) => {
        const attachments: IFileUpload[] = [];
        for (const item of (task.upload_file || []) as any) {
          attachments.push({
            file: null,
            key: getUniqueId(), // Generate a unique key for each file
            path: item,
            type: "LOCAL",
          });
        }
        return {
          ...task,
          upload_file: attachments,
        };
      });

      dispatch(fetchTaskListSuccess(updatedTasks, totalRecords));
    } catch (err: any) {
      dispatch(fetchTaskListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const FETCH_SUB_TASK_LIST_PROGRESS = "FETCH_SUB_TASK_LIST_PROGRESS";
export const FETCH_SUB_TASK_LIST_SUCCESS = "FETCH_SUB_TASK_LIST_SUCCESS";
export const FETCH_SUB_TASK_LIST_FAILED = "FETCH_SUB_TASK_LIST_FAILED";

export const fetchSubTaskListProgress = () =>
  action(FETCH_SUB_TASK_LIST_PROGRESS);
export const fetchSubTaskListSuccess = (
  data: ITaskListState["list"],
  totalRecords: number,
) => action(FETCH_SUB_TASK_LIST_SUCCESS, { data, totalRecords });
export const fetchSubTaskListFailed = () => action(FETCH_SUB_TASK_LIST_FAILED);

export const fetchSubTaskListAsync =
  (parentUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchSubTaskListProgress());

      const searchQuery = `&pageNo=1&itemPerPage=1000`;

      let url = `/task/get-task-list?parent_uuid=${parentUuid}${searchQuery}`;

      const res = await api.get(url);
      const data: ITaskListItem[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchSubTaskListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchSubTaskListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const FETCH_SINGLE_TASK_PROGRESS = "FETCH_SINGLE_TASK_PROGRESS";
export const FETCH_SINGLE_TASK_SUCCESS = "FETCH_SINGLE_TASK_SUCCESS";
export const FETCH_SINGLE_TASK_FAILED = "FETCH_SINGLE_TASK_FAILED";

export const fetchSingleTaskListProgress = () =>
  action(FETCH_SINGLE_TASK_PROGRESS);
export const fetchSingleTaskListSuccess = (data: ITaskListItem) =>
  action(FETCH_SINGLE_TASK_SUCCESS, { data });
export const fetchSingleTaskListFailed = () => action(FETCH_SINGLE_TASK_FAILED);

export const fetchSingleTaskListAsync =
  (
    taskUuid: string,
    type: "task" | "subtask",
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchSingleTaskListProgress());

      let url = `/task/get-task-list?task_uuid=${taskUuid}`;
      const res = await api.get(url);
      const data = res.data.data;
      if (data && data.length > 0) {
        dispatch(fetchSingleTaskListSuccess(data[0]));
      } else {
        dispatch(fetchSingleTaskListFailed());
      }
    } catch (err: any) {
      dispatch(fetchSingleTaskListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const FETCH_PROJECT_TASK_LIST_PROGRESS =
  "FETCH_PROJECT_TASK_LIST_PROGRESS";
export const FETCH_PROJECT_TASK_LIST_SUCCESS =
  "FETCH_PROJECT_TASK_LIST_SUCCESS";
export const FETCH_PROJECT_TASK_LIST_FAILED = "FETCH_PROJECT_TASK_LIST_FAILED";

export const fetchProjectTaskListProgress = () =>
  action(FETCH_PROJECT_TASK_LIST_PROGRESS);
export const fetchProjectTaskListSuccess = (
  data: ITaskListState["list"],
  totalRecords: number,
) => action(FETCH_PROJECT_TASK_LIST_SUCCESS, { data, totalRecords });
export const fetchProjectTaskListFailed = () =>
  action(FETCH_PROJECT_TASK_LIST_FAILED);

export const fetchProjectTaskListAsync =
  (
    queryParams: IQueryParams,
    billing_company_uuid: string,
    taskId: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchProjectTaskListProgress());

      const searchQuery = getSearchQuery(queryParams);

      let url = `/task/get-task-list${searchQuery}`;
      if (billing_company_uuid) {
        url += `&billing_company_uuid=${billing_company_uuid}`;
      }
      if (taskId) {
        url += `&project_uuid=${taskId}`;
      }
      const res = await api.get(url);
      const data: ITaskListItem[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchProjectTaskListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchProjectTaskListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const UPSERT_PROJECT_TASK_SUCCESS = "UPSERT_PROJECT_TASK_SUCCESS";

export const upsertProjectTaskSuccess = (data: ITaskListItem) =>
  action(UPSERT_PROJECT_TASK_SUCCESS, data);

export const upsertTaskListItemAsync =
  (
    data: ITaskListItem,
    onCallback: (isSuccess: boolean, TaskListItem?: ITaskListItem) => void,
    deleteTask?: boolean,
    // file?: any | null,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const keysToOmit =
        data.status === "COMPLETED"
          ? ["create_ts", "insert_ts", "rowId"]
          : ["create_ts", "insert_ts", "rowId", "time_taken"];

      const asPayload = {
        title: data.type_name?.split(" ").join("_") || "",
      };

      const path = await uploadMultipleFiles(
        data.upload_file || "",
        "TASKS",
        asPayload,
      );

      const rest = omit({ ...data, upload_file: path }, keysToOmit);
      const res = await api.post("/task/upsert-task", rest);
      let message = deleteTask
        ? "Task deleted successfully!"
        : "Task saved successfully!";
      onCallback(true, res.data.data);
      dispatch(upsertProjectTaskSuccess(res.data.data));
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: message,
        }),
      );
    } catch (err: any) {
      onCallback(true);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
export const UPSERT_PROJECT_SUB_TASK_SUCCESS =
  "UPSERT_PROJECT_SUB_TASK_SUCCESS";

export const upsertProjectSubTaskSuccess = (data: ITaskListItem) =>
  action(UPSERT_PROJECT_SUB_TASK_SUCCESS, data);

export const upsertSubTaskListItemAsync =
  (
    data: ITaskListItem,
    onCallback: (isSuccess: boolean, TaskListItem?: ITaskListItem) => void,
    // file?: any | null,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const keysToOmit =
        data.status === "COMPLETED"
          ? ["create_ts", "insert_ts", "rowId"]
          : ["create_ts", "insert_ts", "rowId", "time_taken"];

      const asPayload = {
        title: data.type_name?.split(" ").join("_") || "",
      };

      const path = await uploadMultipleFiles(
        data.upload_file || "",
        "TASKS",
        asPayload,
      );

      const rest = omit({ ...data, upload_file: path }, keysToOmit);
      const res = await api.post("/task/upsert-task", rest);
      let message = "Task saved successfully!";
      onCallback(true, res.data.data);
      dispatch(upsertProjectSubTaskSuccess(res.data.data));
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: message,
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const fetchtasklistdetailAsync =
  (
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    searchState: { [key: string]: any },
    billing_company_uuid: string,
    assignTo: string[] | undefined,
    onCallback: (data: ITaskListItem[]) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());

      const { status, ...restSearchState } = searchState || {};
      const allFilters: any[] = [];

      // ✅ Handle status filter
      if (status && status.length > 0) {
        allFilters.push({
          column: ["status"],
          operator: "IN",
          value: status,
          logicalOperator: "AND",
        });
      }

      // ✅ Add advanced filters
      if (searchValue && searchValue.length > 0) {
        allFilters.push(...searchValue);
      }

      // ✅ Handle other searchState fields
      for (const [key, value] of Object.entries(restSearchState)) {
        if (value !== "" && value !== null) {
          let filterObj = {
            column: [key],
            operator: "CONTAINS",
            value,
            logicalOperator: "AND",
          };
          if (key === "transaction_date") {
            filterObj.value = value + "-";
          }
          allFilters.push(filterObj);
        }
      }

      // ✅ Build URL for export (large dataset)
      let finalUrl = `/task/get-task-list?pageNo=1&itemPerPage=100000&from_date=${date.fromDate}&to_date=${date.toDate}`;

      if (allFilters.length > 0) {
        const encoded = encodeURIComponent(JSON.stringify(allFilters, null, 2));
        finalUrl += `&advanceFilter=${encoded}`;
      }

      if (billing_company_uuid) {
        finalUrl += `&billing_company_uuid=${billing_company_uuid}`;
      }

      if (assignTo && assignTo.length > 0) {
        finalUrl += `&assignee_uuid=${assignTo.join(",")}`;
      }

      // ✅ Fetch tasks
      const res = await api.get(finalUrl);
      const data: ITaskListItem[] = res.data.data;

      onCallback(data);
    } catch (err: any) {
      onCallback([]);
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch task details",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertTaskWithAiAsync =
  (
    title: string,
    project_uuid: string,
    onCallback: (isSuccess: boolean, TaskListItem?: IProject) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/task/assign-task-with-ai?project_uuid=${project_uuid}&prompt=${title}`,
      );
      let message = "Task saved successfully!";
      onCallback(true, res.data.data);
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: message,
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_TASK_LIST_ITEM = "CLEAR_TASK_LIST_ITEM";
export const clearTaskListItem = () => action(CLEAR_TASK_LIST_ITEM);
export const CLEAR_SUB_TASK_LIST_ITEM = "CLEAR_SUB_TASK_LIST_ITEM";
export const clearSubTaskListItem = () => action(CLEAR_SUB_TASK_LIST_ITEM);
export const CLEAR_TASK_LIST_STATE = "CLEAR_TASK_LIST_STATE";
export const clearTaskListState = () => action(CLEAR_TASK_LIST_STATE);
export const CLEAR_SINGLE_TASK_LIST_ITEM = "CLEAR_SINGLE_TASK_LIST_ITEM";
export const clearSingleTaskListItem = () =>
  action(CLEAR_SINGLE_TASK_LIST_ITEM);
